import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';

const DynamoDBStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    loadStats();
    
    // Set up auto-refresh every 60 seconds
    const interval = setInterval(loadStats, 60000);
    setRefreshInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const loadStats = async () => {
    try {
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/admin/analytics/dynamodb-stats`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load DynamoDB stats: ${response.statusText}`);
      }

      const data = await response.json();
      setStats(data);

    } catch (err) {
      console.error('Error loading DynamoDB stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toString() || '0';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'CREATING': return '#ffc107';
      case 'UPDATING': return '#17a2b8';
      case 'DELETING': return '#dc3545';
      case 'ERROR': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE': return '✅';
      case 'CREATING': return '🔄';
      case 'UPDATING': return '⚙️';
      case 'DELETING': return '🗑️';
      case 'ERROR': return '❌';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="dynamodb-stats">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading DynamoDB statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dynamodb-stats">
        <div className="error-state">
          <h3>⚠️ Error Loading DynamoDB Stats</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadStats} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dynamodb-stats">
      <div className="stats-header">
        <div>
          <h2>🗄️ DynamoDB Statistics</h2>
          <p>Detailed table metrics and storage information</p>
        </div>
        <div className="stats-actions">
          <button onClick={loadStats} className="refresh-button" disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {stats && (
        <>
          {/* Summary Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">📊</div>
              <div className="metric-content">
                <div className="metric-value">{stats.totals.totalTables}</div>
                <div className="metric-label">Total Tables</div>
                <div className="metric-detail">
                  {stats.totals.healthyTables} healthy • {stats.totals.errorTables} errors
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">📝</div>
              <div className="metric-content">
                <div className="metric-value">{formatNumber(stats.totals.totalItems)}</div>
                <div className="metric-label">Total Items</div>
                <div className="metric-detail">
                  Across all tables
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">💾</div>
              <div className="metric-content">
                <div className="metric-value">
                  {stats.totals.totalSizeMB} MB
                  {Object.values(stats.tables).some(table => table.isEstimated) && 
                    <span className="estimated-badge">~</span>
                  }
                </div>
                <div className="metric-label">Total Storage</div>
                <div className="metric-detail">
                  {formatBytes(stats.totals.totalSizeBytes)}
                  {Object.values(stats.tables).some(table => table.isEstimated) && 
                    <span style={{fontSize: '0.8em', color: '#ffc107'}}> (includes estimates)</span>
                  }
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">⚡</div>
              <div className="metric-content">
                <div className="metric-value">
                  {stats.totals.healthyTables === stats.totals.totalTables ? 'Healthy' : 'Issues'}
                </div>
                <div className="metric-label">Overall Status</div>
                <div className="metric-detail">
                  All systems operational
                </div>
              </div>
            </div>
          </div>

          {/* Individual Table Stats */}
          <div className="dashboard-section">
            <h3>📋 Table Details</h3>
            <div className="table-stats-grid">
              {Object.entries(stats.tables).map(([key, table]) => (
                <div key={key} className="table-stat-card">
                  <div className="table-stat-header">
                    <div className="table-info">
                      <h4>{table.displayName}</h4>
                      <span className="table-name">{table.tableName}</span>
                    </div>
                    <div className="table-status">
                      <span 
                        className="status-badge"
                        style={{ 
                          color: getStatusColor(table.tableStatus),
                          backgroundColor: getStatusColor(table.tableStatus) + '20'
                        }}
                      >
                        {getStatusIcon(table.tableStatus)} {table.tableStatus}
                      </span>
                    </div>
                  </div>

                  {table.error ? (
                    <div className="table-error">
                      <p>❌ Error: {table.error}</p>
                    </div>
                  ) : (
                    <div className="table-metrics">
                      <div className="table-metric-row">
                        <div className="table-metric">
                          <span className="metric-label">Items</span>
                          <span className="metric-value">{formatNumber(table.itemCount)}</span>
                        </div>
                        <div className="table-metric">
                          <span className="metric-label">Size</span>
                          <span className="metric-value">
                            {table.tableSizeMB} MB
                            {table.isEstimated && <span className="estimated-badge">~</span>}
                          </span>
                        </div>
                      </div>

                      <div className="table-metric-row">
                        <div className="table-metric">
                          <span className="metric-label">Avg Item Size</span>
                          <span className="metric-value">
                            {formatBytes(table.avgItemSizeBytes)}
                            {table.isEstimated && <span className="estimated-badge">~</span>}
                          </span>
                        </div>
                        <div className="table-metric">
                          <span className="metric-label">Status</span>
                          <span className="metric-value">{table.tableStatus}</span>
                        </div>
                      </div>

                      {table.creationDateTime && (
                        <div className="table-creation-date">
                          <span className="metric-label">Created:</span>
                          <span className="metric-value">
                            {new Date(table.creationDateTime).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Last Updated */}
          <div className="stats-footer">
            <p>Last updated: {new Date(stats.timestamp).toLocaleString()}</p>
            <p>Auto-refresh: Every 60 seconds</p>
          </div>
        </>
      )}
    </div>
  );
};

export default DynamoDBStats;